/**
 * V317.2: Play Integrity – "Bare Metal" pass-through. NO auth/session logic. If caller says 'purchase', we send 'purchase'.
 * V317.1: Bare Metal fix: NO auth-state logic modifying purpose.
 * Only caller of verify-play-integrity; purposes: bootstrap, login, purchase, self_test.
 * Web: testable via ?pi_debug=1 with debug token. Native Android: IntegrityService (Play Integrity bridge).
 */

import { Capacitor } from '@capacitor/core';
import { supabase } from '@/lib/supabaseGuard';
import { IntegrityService } from '@/services/security/IntegrityService';

const LOG_TAG = '[PlayIntegrity]';
const VERSION = 'V317.2';

/** Nonce compatible with old WebViews/Safari */
function generateNonce(): string {
  const c: Crypto | undefined = (globalThis as any).crypto;
  if (c && typeof (c as any).randomUUID === 'function') return (c as any).randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isWebDebug(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('pi_debug') === '1';
}

function isNativeAndroid(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

function shouldRun(): boolean {
  return isNativeAndroid() || isWebDebug();
}

export interface PlayIntegrityRunResult {
  ok: boolean;
  reason?: string;
  check_id?: string | null;
}

/**
 * V317.2 BARE METAL: Purely pass-through. No auth/session checks. Verbatim purpose.
 */
async function run(purpose: string, source?: string): Promise<PlayIntegrityRunResult | null> {
  const purposeToSend = purpose;
  const client_source = source ?? 'unknown';

  console.log(`[PlayIntegrity] V317.2 run purpose=${purposeToSend} source=${client_source}`);

  if (!shouldRun()) {
    console.log(`${LOG_TAG} skipped (not native Android or pi_debug) purpose=${purposeToSend}`);
    return null;
  }

  const nonce = generateNonce();
  let integrityToken: string;

  if (isWebDebug()) {
    integrityToken = 'debug_invalid_token';
  } else {
    const result = await IntegrityService.requestIntegrityToken(nonce);
    if (!result.token) {
      console.log(`${LOG_TAG} skipped purpose=${purposeToSend} (native token missing)`);
      return null;
    }
    integrityToken = result.token;
  }

  try {
    const { data, error } = await supabase.functions.invoke('verify-play-integrity', {
      body: {
        integrityToken,
        nonce,
        purpose: purposeToSend,
        expectedPurpose: purposeToSend,
        client_source,
      },
      headers: isWebDebug() ? { 'X-PI-DEBUG': '1' } : undefined,
    });

    if (error) {
      console.warn(`${LOG_TAG} invoke error`, error);
      return { ok: false, reason: 'invoke_error', check_id: null };
    }

    const ok = (data as { ok?: boolean })?.ok ?? false;
    const reason = (data as { reason?: string })?.reason ?? 'unknown';
    const check_id = (data as { check_id?: string | null })?.check_id ?? null;
    return { ok, reason, check_id };
  } catch (e) {
    console.warn(`${LOG_TAG} run failed`, e);
    return { ok: false, reason: 'exception', check_id: null };
  }
}

function debugState(): void {
  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();
  console.log(`${LOG_TAG} debugState platform=${platform} isNativePlatform=${isNative} isNativeAndroid=${isNativeAndroid()} isWebDebug=${isWebDebug()} shouldRun=${shouldRun()}`);
}

export const PlayIntegrityService = { shouldRun, run, debugState };
